import React, { useEffect, useReducer, useState } from "react";

import openSocket from "../../services/socket-io";

import {
  AddCircleOutline,
  DeleteOutline,
  Edit,
  KeyboardArrowUp,
  KeyboardArrowDown,
  CheckCircle
} from "@material-ui/icons";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip
} from "@material-ui/core";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import QueueModal from "../../components/QueueModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [...state, queue];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("queue", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);
  const [filteredQueues, setFilteredQueues] = useState([]);
  const [rowsAltered, setRowsAltered] = useState(false);
  useEffect(() => {
    // Verifica se queues está definido antes de aplicar a filtragem
    if (queues) {
      setFilteredQueues(queues.filter(queue => queue.parentId === null));
    }
  }, [queues]);
  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("queues.notifications.queueDeleted"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };
  const moveRow = (fromIndex, toIndex) => {
    setRowsAltered(true);
    const updatedQueues = [...filteredQueues];
    const [movedRow] = updatedQueues.splice(fromIndex, 1);
    updatedQueues.splice(toIndex, 0, movedRow);
    setFilteredQueues(updatedQueues);
  };
 
  const updatePositions = async () => {
    try {
      let form = [];
      filteredQueues.forEach((item, index) => {
        form.push({id: item.id, position: index + 1 })
      })
      await api.post(`/queuepositions`, form);
      toast.success("Ordenação redefinida com sucesso!");
      setRowsAltered(false);
    } catch (err) {
      toastError(err);
    }
  };
  const [open, setOpen] = React.useState(false);

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        nextId={filteredQueues?.length + 1}
      />
      <MainHeader>
        <Title>{i18n.t("queues.title")} ({filteredQueues.length})</Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Salvar Ordenação">
            <Button
              disabled={!rowsAltered}
              variant="contained"
              color="primary"
              onClick={updatePositions}
            >
              <CheckCircle />
            </Button>
          </Tooltip>
          <Tooltip title={i18n.t("queues.buttons.add")}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenQueueModal}
            >
              <AddCircleOutline />
            </Button>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("queues.table.id")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.color")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.greeting")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.actions")}
              </TableCell>
              <TableCell align="center">
                Ordenar
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQueues.map((queue, index) => (
              <TableRow key={queue.id}>
                <TableCell align="center">{queue.id}</TableCell>
                <TableCell align="center">{queue.name}</TableCell>
                <TableCell align="center">
                  <div className={classes.customTableCell}>
                    <span
                      style={{
                        backgroundColor: queue.color,
                        width: 20,
                        height: 20,
                        alignSelf: "center",
                        borderRadius: 10
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className={classes.customTableCell}>
                    <Typography
                      style={{ width: 300, align: "center" }}
                      noWrap
                      variant="body2"
                    >
                      {queue.greetingMessage}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditQueue(queue)}
                  >
                    <Edit color="secondary" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedQueue(queue);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline color="secondary" />
                  </IconButton>

                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => moveRow(index, index - 1)}
                    disabled={index === 0}
                  >
                    <KeyboardArrowUp />
                  </IconButton>

                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => moveRow(index, index + 1)}
                    disabled={index === filteredQueues.length - 1}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Queues;
